<template>
  <aside
    class="pc-side"
    :class="{
      'pc-side--active': sideMode === 'formal',
      'pc-side--trial': sideMode === 'trial',
      'pc-side--expired': isServiceExpiredLayout,
    }"
  >
    <div class="pc-profile">
      <div class="pc-profile__avatar-wrap">
        <img
          v-if="showCrown"
          class="pc-profile__crown"
          :src="crownIconSrc"
          alt=""
          aria-hidden="true"
        />
        <div class="avatar" :class="{ 'avatar--paid': hasPaidService }">
          <div
            class="avatar__media"
            :class="{ 'avatar__media--uploaded': avatarUrl }"
          >
            <img
              class="avatar__img"
              :class="{ 'avatar__img--uploaded': avatarUrl }"
              :src="avatarUrl || defaultAvatar"
              alt=""
            />
          </div>
        </div>
        <span
          class="pc-profile__tag"
          :class="verified ? 'pc-profile__tag--ok' : 'pc-profile__tag--warn'"
        >
          {{ verified ? "已实名" : "未实名" }}
        </span>
      </div>
      <p class="pc-profile__name">{{ displayName }}</p>
      <a
        href="javascript:;"
        class="pc-profile__link"
        @click.prevent="$emit(verified ? 'realname-record' : 'go-realname')"
      >
        {{ verified ? "实名记录 >" : "去实名 >" }}
      </a>
    </div>

    <!-- 还未申请试用（与 claimTrial 一致：hasService=false 且 canApplyTrial=true） -->
    <div v-if="showApplyTrialCard" class="vip-box vip-box--apply">
      <div class="vip-body">
        <div class="vip-info">
          <p class="vip-line1">
            领取后立享<span class="vip-line2__days">{{ trialDays }}</span
            ><span class="vip-line2__days">天</span>免费试用
          </p>
        </div>
        <button
          type="button"
          class="vip-btn vip-btn--claim"
          @click="$emit('apply-trial')"
        >
          立即领取
        </button>
      </div>
    </div>

    <!-- 正在试用：倒计时 -->
    <div v-else-if="sideMode === 'trial'" class="trial-box">
      <p class="trial-label">试用倒计时</p>
      <div class="trial-time">
        <span class="time-box">{{ countdown.days }}</span>
        <span class="time-unit">天</span>
        <span class="time-box">{{ countdown.hours }}</span>
        <span class="time-unit">时</span>
        <span class="time-box">{{ countdown.minutes }}</span>
        <span class="time-unit">分</span>
        <span class="time-box">{{ countdown.seconds }}</span>
        <span class="time-unit">秒</span>
      </div>
      <div class="tip-box">
        <img
          class="tip-icon"
          src="../img/attention.png"
          alt=""
          aria-hidden="true"
        />
        <div class="tip-body">
          <p class="tip-head">温馨提示</p>
          <p class="tip-text">{{ trialTipText }}</p>
        </div>
      </div>
      <button
        type="button"
        class="btn-buy"
        @click="$emit('purchase')"
      >
        购买服务
      </button>
      <p
        v-if="!verified"
        class="trial-hint"
        role="button"
        tabindex="0"
        @click="$emit('go-realname')"
        @keydown.enter.prevent="$emit('go-realname')"
      >
        购买正式服务前请先进行实名认证
      </p>
    </div>

    <!-- VIP 卡片：已购买 / 试用到期 / 服务到期（未开通且不可领试用时不展示） -->
    <div
      v-else-if="sideMode !== 'apply'"
      class="vip-box"
      :class="{
        'vip-box--expired': sideMode === 'expired',
        'vip-box--trial-expired': sideMode === 'trial-expired',
      }"
    >
      <div class="vip-body">
        <div class="vip-info">
          <p
            class="vip-line1"
            :class="{
              'is-red': sideMode === 'expired',
              'is-trial-expired': sideMode === 'trial-expired',
            }"
          >
            {{ vipLine1 }}
          </p>
          <p v-if="sideMode === 'formal' && expireText" class="vip-date-text">
            {{ expireText }}
          </p>
          <p v-else-if="isServiceExpiredLayout" class="vip-line2">
            {{ vipLine2 }}
          </p>
        </div>
        <button
          type="button"
          class="vip-btn"
          @click="$emit('purchase')"
        >
          {{ vipBtnText }}
        </button>
      </div>
    </div>

    <nav class="pc-nav">
      <a
        v-for="item in menuList"
        :key="item.key"
        href="javascript:;"
        class="nav-item"
        :class="{ active: activeMenu === item.key }"
        @click.prevent="$emit('change-menu', item.key)"
      >
        <img
          class="nav-icon"
          :src="getNavIcon(item.key)"
          alt=""
          aria-hidden="true"
        />
        <span class="nav-label">{{ item.label }}</span>
        <span class="nav-arrow"><i class="el-icon-arrow-right"></i></span>
      </a>
    </nav>
  </aside>
</template>

<script>
/**
 * 个人中心左侧栏：用户信息、试用/VIP 状态、导航菜单
 * UI 分支由 subscriptionState.view 驱动（见 subscriptionService.buildView）
 */
import { MENU_LIST, VIEW_MODE } from "../config";
import { isRealnameVerified, parseTrialEndTimestamp } from "../api";
import defaultAvatar from "../img/avatar.png";
import crownIcon from "../img/crown.png";
import crownIconExpired from "../img/crown-h.png";
import {
  subscriptionState,
  realnameState,
} from "@/view/user/indivSession/indivSessionService";
import {
  isTrialSubscriptionExpired,
  getSubscriptionExpireTime,
} from "@/view/user/subscriptionService/subscriptionService";
import { userAccountState } from "@/view/user/userAccount/userAccountService";

const NAV_ICONS = {
  basic: {
    default: require("../img/icon/basic.png"),
    active: require("../img/icon/basic-active.png"),
  },
  orders: {
    default: require("../img/icon/orders.png"),
    active: require("../img/icon/orders-active.png"),
  },
  invoice: {
    default: require("../img/icon/invoice.png"),
    active: require("../img/icon/invoice-active.png"),
  },
  title: {
    default: require("../img/icon/title.png"),
    active: require("../img/icon/title-active.png"),
  },
};

export default {
  name: "PcSidebar",
  props: {
    activeMenu: {
      type: String,
      default: "basic",
    },
    userName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      menuList: MENU_LIST,
      navIcons: NAV_ICONS,
      defaultAvatar,
      countdown: { days: "00", hours: "00", minutes: "00", seconds: "00" },
      countdownTimer: null,
    };
  },
  computed: {
    /** 原始订阅数据，来自 GET /status，由 refreshIndivSession 刷新 */
    subscription() {
      return subscriptionState.status;
    },
    /** 由 /status 推导的到期日、剩余天数等（buildActive） */
    active() {
      return subscriptionState.activeData;
    },
    /** 实名状态（indiv 全局会话） */
    realname() {
      return realnameState.status;
    },
    verified() {
      return isRealnameVerified(this.realname);
    },
    /** 订阅 UI 视图模型，由 refreshIndivSession → applySubscriptionStatus 写入 */
    view() {
      return subscriptionState.view;
    },
    /** 侧栏主展示模式，等价 view.mode */
    sideMode() {
      return this.view.mode;
    },
    /**
     * 是否展示「领取试用」卡片
     * 与 claimTrial 一致：loaded 且 hasService=false 且 canApplyTrial=true
     */
    showApplyTrialCard() {
      if (!subscriptionState.loaded) return false;
      const s = this.subscription;
      return Boolean(s && s.hasService === false && s.canApplyTrial === true);
    },
    /** 已购正式服务，或试用期内已预购：头像金色边框 */
    hasPaidService() {
      return this.view.vip;
    },
    /** 正式服务已到期时展示灰色皇冠 */
    isFormalServiceExpired() {
      return this.sideMode === VIEW_MODE.EXPIRED;
    },
    showCrown() {
      return this.hasPaidService || this.isFormalServiceExpired;
    },
    crownIconSrc() {
      return this.isFormalServiceExpired ? crownIconExpired : crownIcon;
    },
    displayName() {
      return this.userName || "—";
    },
    avatarUrl() {
      const img =
        (userAccountState.profile && userAccountState.profile.selfImage) ||
        (this.$store.state.usermsg && this.$store.state.usermsg.selfImage);
      return img ? `${this.$store.state.api}${img}` : "";
    },
    trialDays() {
      const d = this.subscription && this.subscription.trialDays;
      return d != null ? Number(d) : 14;
    },
    trialEndTime() {
      return (this.subscription && this.subscription.trialEndTime) || "";
    },
    /** 试用到期 / 正式到期 → 灰色侧栏背景 */
    isServiceExpiredLayout() {
      return this.view.grayLayout;
    },
    vipLine1() {
      if (this.sideMode === VIEW_MODE.FORMAL) {
        return "服务到期时间";
      }
      if (this.sideMode === VIEW_MODE.TRIAL_EXPIRED) {
        return "您的试用已到期";
      }
      return "您的服务已到期";
    },
    expireText() {
      const sub = this.subscription;
      const expire =
        (this.active && this.active.expireTime) ||
        (sub && getSubscriptionExpireTime(sub)) ||
        "";
      const dateOnly = expire ? String(expire).trim().split(/\s+/)[0] : "";
      return dateOnly ? `${dateOnly} 到期` : "";
    },
    vipLine2() {
      return "购买服务后方可使用";
    },
    vipBtnText() {
      if (
        this.sideMode === VIEW_MODE.TRIAL_EXPIRED ||
        this.sideMode === VIEW_MODE.EXPIRED
      ) {
        return "立即购买";
      }
      return "续费";
    },
    trialEndTimestamp() {
      return parseTrialEndTimestamp(this.trialEndTime);
    },
    /** 试用倒计时区域温馨提示文案 */
    trialTipText() {
      if (isTrialSubscriptionExpired(this.subscription)) {
        return "您的试用已到期，请购买正式服务继续使用。";
      }
      return "请于试用结束前购买正式服务，以免影响您的正常使用。";
    },
  },
  watch: {
    "view.countdown": {
      immediate: true,
      handler(show) {
        if (show) {
          this.startCountdown();
        } else {
          this.stopCountdown();
        }
      },
    },
    trialEndTime() {
      if (this.view.countdown) this.tickCountdown();
    },
  },
  beforeDestroy() {
    this.stopCountdown();
  },
  methods: {
    /** 导航菜单图标：选中态与默认态 */
    getNavIcon(key) {
      const icons = this.navIcons[key];
      if (!icons) return "";
      return this.activeMenu === key ? icons.active : icons.default;
    },
    /** 数字补零为两位 */
    padTwo(n) {
      return String(n).padStart(2, "0");
    },
    /** 根据试用结束时间更新倒计时显示 */
    tickCountdown() {
      const diff = Math.max(0, this.trialEndTimestamp - Date.now());
      this.countdown = {
        days: this.padTwo(Math.floor(diff / 86400000)),
        hours: this.padTwo(Math.floor((diff % 86400000) / 3600000)),
        minutes: this.padTwo(Math.floor((diff % 3600000) / 60000)),
        seconds: this.padTwo(Math.floor((diff % 60000) / 1000)),
      };
    },
    /** 启动试用倒计时定时器 */
    startCountdown() {
      this.stopCountdown();
      this.tickCountdown();
      this.countdownTimer = setInterval(() => this.tickCountdown(), 1000);
    },
    /** 停止试用倒计时定时器 */
    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
  },
};
</script>

<style scoped src="./style.css"></style>



// WEBPACK FOOTER //
// src/view/client/personal-center/sidebar/index.vue